import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  Bell, ArrowLeft, Plus, Trash2, Loader, AlertCircle,
  AlertTriangle, Info
} from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";

export default function ChurchAnnouncementsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const { data } = await axios.get(`${API}/church/announcements`);
      setAnnouncements(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm(t('confirm_delete_announcement'))) return;
    
    try {
      await axios.delete(`${API}/church/announcements/${announcementId}`);
      setAnnouncements(announcements.filter(a => a.id !== announcementId));
    } catch (e) {
      alert(e.response?.data?.detail || t('could_not_delete'));
    }
  };

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case "urgent":
        return { bg: "bg-red-50", border: "border-red-200", icon: AlertCircle, color: "#EF476F" };
      case "important":
        return { bg: "bg-yellow-50", border: "border-yellow-200", icon: AlertTriangle, color: "#FFD166" };
      default:
        return { bg: "bg-blue-50", border: "border-blue-200", icon: Info, color: "#118AB2" };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <Loader size={48} className="animate-spin" color="#EF476F" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-10"
        style={{ background: "linear-gradient(135deg, #EF476F 0%, #FFD166 100%)" }}>
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate("/church")}
            className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl mb-4">
            <ArrowLeft size={16} /> {t('back')}
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-2xl p-3">
                <Bell size={28} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('announcements')}
                </h1>
                <p className="text-white/70 text-sm font-medium">{t('send_messages_to_families')}</p>
              </div>
            </div>
            <button onClick={() => setShowCreateModal(true)}
              className="bg-white text-[#EF476F] font-black px-4 py-2 rounded-xl flex items-center gap-1"
              data-testid="create-announcement-btn">
              <Plus size={18} /> {t('new')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-5">
        {announcements.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
            <Bell size={48} className="mx-auto mb-3 opacity-40" color="#EF476F" />
            <p className="text-gray-500 font-medium">{t('no_announcements_yet')}</p>
            <p className="text-gray-400 text-sm">{t('send_first_message')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {announcements.map((announcement) => {
              const style = getPriorityStyle(announcement.priority);
              const Icon = style.icon;
              
              return (
                <div key={announcement.id} 
                  className={`bg-white rounded-2xl shadow-md overflow-hidden border-l-4`}
                  style={{ borderColor: style.color }}>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon size={18} color={style.color} />
                        <p className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                          {announcement.title}
                        </p>
                      </div>
                      <button onClick={() => handleDelete(announcement.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600"
                        data-testid={`delete-announcement-${announcement.id}`}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3">{announcement.message}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${style.bg}`}
                        style={{ color: style.color }}>
                        {announcement.priority.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(announcement.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateAnnouncementModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={() => { setShowCreateModal(false); loadAnnouncements(); }}
        />
      )}
    </div>
  );
}

function CreateAnnouncementModal({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    message: "",
    priority: "normal"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/church/announcements`, form);
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create announcement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl w-full max-w-md p-6">
        <h2 className="text-xl font-black text-[#073B4C] mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
          New Announcement
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#EF476F] outline-none"
              placeholder="e.g., Sunday Service Update"
              required
              data-testid="announcement-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Message</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#EF476F] outline-none resize-none"
              rows={4}
              placeholder="Your message to all church families..."
              required
              data-testid="announcement-message-input"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Priority</label>
            <div className="flex gap-2">
              {["normal", "important", "urgent"].map((p) => (
                <button key={p} type="button"
                  onClick={() => setForm({ ...form, priority: p })}
                  className={`flex-1 py-2 rounded-xl font-bold text-sm capitalize transition-all ${
                    form.priority === p 
                      ? p === "urgent" ? "bg-red-500 text-white" 
                        : p === "important" ? "bg-yellow-500 text-white" 
                        : "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                  data-testid={`priority-${p}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm font-medium p-3 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold border-2 border-gray-200 text-gray-600">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-3 rounded-xl font-bold text-white disabled:opacity-50"
              style={{ background: "#EF476F" }}
              data-testid="submit-announcement">
              {loading ? <Loader size={20} className="mx-auto animate-spin" /> : "Send"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
